import { reactive, watch, ref } from '@vue/composition-api';
import usePagination from '@/views/components/pagination/usePagination';
import useValidationModal from '@/@core/components/base-modal/hooks/useValidationModal';
import useFilterHeaderTable from '@/views/components/filter-header-table/useFilterHeaderTable';
import useDate from '@/utils/hooks/useDate';

import { setBannerModel } from '@/models/banner/banner';
import store from '@/store';

export default function useCopyBannerModal(props) {
  const fields = [
    { key: 'seq', label: 'No.' },
    { key: 'set_name', label: 'Set Name' },
    { key: 'valid_from', label: 'Valid From' },
    { key: 'valid_through', label: 'Valid Through' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '' },
  ];

  const setBannerListTable = reactive({ payload: [], rows: 0 });
  const loading = reactive({ tableData: false, api: true });
  const keyChange = ref(0);

  const {
    fetchAssignSetBannerListModel,
    assignToSetBannerModel,
  } = setBannerModel();

  const { pagination } = usePagination();
  const { openDefaultErrorModal } = useValidationModal();
  const { resolveDateWithSeperator } = useDate();
  const { filters, searchValue, onSearch } = useFilterHeaderTable();

  const onModalOpen = () => {
    pagination.currentPage = 1;

    fetchSetBannerList();
  };

  const fetchSetBannerList = (loadingAction = true) => {
    fetchAssignSetBannerListModel.payload.Banner_Id = props.bannerId;
    fetchAssignSetBannerListModel.payload.Page = pagination.currentPage;
    fetchAssignSetBannerListModel.payload.Limit = pagination.perPage;
    fetchAssignSetBannerListModel.payload.Filters = filters;
    loading.api = true;

    if (loadingAction) {
      loading.tableData = true;
    }

    store
      .dispatch('api/apiRequest', fetchAssignSetBannerListModel)
      .then((response) => {
        setBannerListTable.payload = response.data.payload;
        setBannerListTable.rows = setBannerListTable.payload.length;
        pagination.rows = response.data.payload;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.api = false;
        loading.tableData = false;
      });
  };

  const copyToSetBanner = (buttonProperties, setBanner) => {
    assignToSetBannerModel.payload.Banner_Id = props?.bannerId;
    assignToSetBannerModel.payload.Set_Banner_Id = setBanner.set_banner_id;
    buttonProperties.loading = true;
    buttonProperties.disabled = true;

    store
      .dispatch('api/apiRequest', assignToSetBannerModel)
      .then((response) => {
        setBannerListTable.rows--;

        if (setBannerListTable.rows <= 0) {
          return pagination.currentPage--;
        }
        buttonProperties.loading = false;

        fetchSetBannerList(false);
      })
      .catch((err) => {
        openDefaultErrorModal(err);
        buttonProperties.loading = false;
        buttonProperties.disabled = false;
      })
      .finally(() => {
        keyChange.value++;
      });
  };

  const resolveBannerStatusLabel = (status) => {
    switch (status) {
      case true:
        return 'Active';
      case false:
        return 'Inactive';

      default:
        return 'Unknown';
    }
  };

  const onPageChange = (page) => {
    pagination.currentPage = page;
  };

  const onSearchSetBanner = (searchQuery) => {
    searchValue.query = searchQuery;
    onSearch();
  };

  watch(
    filters,
    () => {
      if (pagination.currentPage !== 1) {
        return (pagination.currentPage = 1);
      }

      fetchSetBannerList();
    },
    { deep: true }
  );

  watch(
    pagination,
    () => {
      if (!loading.api) {
        fetchSetBannerList();
      }
    },
    { deep: true }
  );

  return {
    fields,
    setBannerListTable,
    pagination,
    keyChange,
    loading,
    onModalOpen,
    resolveBannerStatusLabel,
    copyToSetBanner,
    onPageChange,
    resolveDateWithSeperator,
    onSearchSetBanner,
  };
}
